/**
 * Hinweis unter einer KIAN-Antwort, für die das Werkzeug-Budget nicht gereicht hat.
 *
 * KIAN darf je Nachricht nur eine begrenzte Zahl Abfragen machen (Einstellungen → Sprachmodell).
 * Aufträge über viele Tickets („bei den MBM-Tickets Bearbeiter und Status setzen") brauchen mehr —
 * ein Admin hebt das Budget für dieses eine Gespräch an und lässt die Antwort neu erzeugen.
 * Techniker sehen, dass es am Budget lag und wen sie fragen können.
 */
import { useState, type ReactNode } from 'react';
import { Button, Group, Paper, Text } from '@mantine/core';
import { IconGauge, IconRefresh } from '@tabler/icons-react';
import type { SessionToolBudget } from '../../lib/api/domains/assistant';

interface ToolBudgetNoticeProps {
  /** Budget, mit dem die Antwort lief */
  usedBudget: number | null;
  /** Aktueller Stand des Gesprächs */
  toolBudget?: SessionToolBudget;
  canRaise: boolean;
  readOnly?: boolean;
  /** Budget setzen (`null` = unverändert lassen) und die Antwort neu erzeugen */
  onRaise?: (perMessage: number | null) => Promise<void>;
}

export function ToolBudgetNotice({ usedBudget, toolBudget, canRaise, readOnly = false, onRaise }: ToolBudgetNoticeProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (perMessage: number | null) => {
    if (!onRaise) return;
    setBusy(true);
    setError(null);
    try {
      await onRaise(perMessage);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Budget konnte nicht geändert werden');
    } finally {
      setBusy(false);
    }
  };

  // Seit der Antwort schon angehoben (Menü oder anderer Tab) — dann nur noch neu beantworten
  const current = toolBudget?.perMessage ?? null;
  const alreadyRaised = current != null && usedBudget != null && current > usedBudget;
  const next = toolBudget?.nextStep ?? null;
  const usedLabel = usedBudget != null ? ` (${usedBudget} Abfragen je Nachricht)` : '';

  let action: ReactNode = null;
  if (!readOnly && onRaise) {
    if (alreadyRaised) {
      action = (
        <Button size="xs" variant="light" color="cyberOrange" leftSection={<IconRefresh size={14} />} loading={busy} onClick={() => void run(null)}>
          Mit {current} Abfragen neu beantworten
        </Button>
      );
    } else if (canRaise && next != null) {
      action = (
        <Button size="xs" variant="light" color="cyberOrange" leftSection={<IconGauge size={14} />} loading={busy} onClick={() => void run(next)}>
          Auf {next} erhöhen &amp; neu beantworten
        </Button>
      );
    }
  }

  let hint: string;
  if (alreadyRaised) hint = `Für dieses Gespräch gelten jetzt ${current} Abfragen je Nachricht.`;
  else if (canRaise && next == null) hint = 'Das ist schon der Höchstwert — den Auftrag besser in Teilen stellen.';
  else if (canRaise) hint = 'Gilt nur für dieses Gespräch, die Einstellung für alle bleibt.';
  else hint = 'Ein Admin kann das Budget für dieses Gespräch erhöhen — oder den Auftrag in kleineren Teilen stellen.';

  return (
    <Paper withBorder radius="md" p="xs" mt="xs" style={{ borderColor: 'var(--mantine-color-yellow-8)' }}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <IconGauge size={16} color="var(--mantine-color-yellow-6)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" fw={600}>
            Werkzeug-Budget ausgeschöpft{usedLabel}
          </Text>
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
          {action ? <Group mt={6}>{action}</Group> : null}
          {error ? (
            <Text size="xs" c="red" mt={4}>
              {error}
            </Text>
          ) : null}
        </div>
      </Group>
    </Paper>
  );
}
